import React, { useState, useEffect, useRef } from 'react';
import { planningOrauxAPI, brandingAPI, departementsAPI } from '../services/api';
import { chargerImagePourPdf } from '../services/planningPdfCommun';
import { destinatairesConvocation, genererConvocations } from '../services/planningOrauxConvocationPdf';
import StageFenetrePromotion from './StageFenetrePromotion';
import FenetreModeleCourriel from './FenetreModeleCourriel';
import { MODELES as MODELES_COURRIEL, LIBELLES_CC, NOTE as NOTE_COURRIEL } from './PlanningOrauxCourriels';
import './SuiviStage.css';

/**
 * PLANNING ORAUX — « ENVOYER LES CONVOCATIONS » (lot PO.5b-2, D15, D28 ; validé le 2026-10-16, « ok je valide les 6 points »)
 * =========================================================================================================================
 * Le menu (aux candidats · aux maîtres — seulement s'il y en a · aux jurys), puis LA FENÊTRE D'ENVOI GROUPÉ DU STAGE
 * (StageFenetrePromotion : une ligne par message, étiquettes, aperçu message par message, essai, tâche de fond, bilan),
 * branchée sur les routes du Planning oraux par son `api`.
 *
 * ⚠️ Les convocations PDF sont fabriquées ICI, au moment de lancer, pour les SEULES lignes qui partent, d'après le planning
 * RELU à l'instant (et ses jetons de vérification) ; le serveur refuse si le planning a changé depuis l'ouverture de la
 * fenêtre (« rouvrez-la »). Les logos sont réduits à leur taille d'impression (point 2 validé : ~4× plus léger).
 * Sans serveur d'envoi : la liste « un par un » (point 6 validé) — la messagerie s'ouvre pour une personne, sa convocation
 * se télécharge pour être jointe à la main ; le journal note « préparé ».
 */
const erreurDe = (e) => e?.response?.data?.error || e?.message || "L'opération a échoué";
/** Une erreur au format de la fenêtre du Stage (elle lit `response.data`). */
const enErreurFenetre = (message, detail = null) => Object.assign(new Error(message), { response: { data: { error: message, detail } } });
const base64De = (pdf) => { const b = new Uint8Array(pdf.output('arraybuffer')); let s = ''; for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode.apply(null, b.subarray(i, i + 0x8000)); return btoa(s); };

/**
 * Ce qu'il faut pour fabriquer les convocations hors de l'onglet Documents (onglet Participation, PO.5p) : l'en-tête du
 * département, les modèles de convocation, les logos réduits (160 px). `null` tant que ce n'est pas chargé.
 */
export function useOutilsConvocation(departementId, logoDepartement, codeDept) {
    const [outils, setOutils] = useState(null);
    useEffect(() => {
        let vivant = true;
        (async () => {
            const b = await brandingAPI.get().catch(() => ({ data: {} }));
            const e = await departementsAPI.getEntete(departementId).catch(() => ({ data: null }));
            const mc = await planningOrauxAPI.modelesConvocation(departementId).catch(() => ({ data: null }));
            const [logoUniversite, logo] = await Promise.all([chargerImagePourPdf(b.data?.logo_universite, 160), chargerImagePourPdf(logoDepartement, 160)]);
            if (vivant) setOutils({ entete: e.data?.entete ? e.data : null, modelesCv: mc.data, logos: { logo, logoUniversite }, codeDept });
        })();
        return () => { vivant = false; };
    }, [departementId, logoDepartement, codeDept]);
    return outils;
}

/**
 * PO.5c (D16) — « PRÉVENIR LES CONCERNÉS » : quand le planning a changé depuis un envoi, le bandeau dit combien de
 * personnes ont reçu quelque chose qui n'est plus vrai ; « Prévenir… » rouvre la fenêtre avec ELLES SEULES cochées et un
 * message de modification. Les passages RETIRÉS (remis « à placer », retirés, enseignant sorti des jurys) : un message
 * d'annulation préparé dans la messagerie, un par un. `cle` : relire après un envoi.
 */
export function BandeauPrevenir({ departementId, planning, modifiable, cle = 0, onPrevenir }) {
    const [c, setC] = useState(null);
    const [ouvert, setOuvert] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [n, setN] = useState(0);
    useEffect(() => {
        let vivant = true;
        planningOrauxAPI.concernes(planning.id, departementId).then((r) => { if (vivant) setC(r.data); }).catch(() => { if (vivant) setC(null); });
        return () => { vivant = false; };
    }, [planning, departementId, cle, n]);
    if (!c || !c.total) return null;
    const LIB = { candidat: ['étudiant', 'étudiants', 'Prévenir les étudiants'], maitre: ['maître', 'maîtres', 'Prévenir les maîtres'], jury: ['enseignant', 'enseignants', 'Prévenir les enseignants'] };
    const changes = Object.keys(LIB).filter((g) => c[g].changes.length);
    const retires = Object.keys(LIB).flatMap((g) => c[g].retires.map((x) => ({ ...x, genre: g })));
    const annuler = async (x) => {
        setErreur(null);
        try { const r = await planningOrauxAPI.annulationEnvoi(planning.id, departementId, x.envoi_id); window.location.href = r.data.mailto; setN((k) => k + 1); }
        catch (e) { setErreur(e?.response?.data?.error || "L'annulation n'a pas pu être préparée"); }
    };
    return (
        <div className="po-prevenir">
            <i className="fa-solid fa-triangle-exclamation" />
            <div style={{ flex: 1 }}>
                <b>Le planning a changé depuis l'envoi</b>{changes.length > 0 && <> : {changes.map((g) => `${c[g].changes.length} ${c[g].changes.length > 1 ? LIB[g][1] : LIB[g][0]}`).join(', ')} à prévenir</>}
                {retires.length > 0 && <> · <button type="button" className="po-lien" onClick={() => setOuvert((o) => !o)}>{retires.length} passage{retires.length > 1 ? 's' : ''} retiré{retires.length > 1 ? 's' : ''} depuis l'envoi {ouvert ? '▴' : '▾'}</button></>}
                {modifiable && changes.length > 0 && <div className="po-prevenir-act">{changes.map((g) => (
                    <button key={g} type="button" className="po-btn po-btn-or po-btn-sm" onClick={() => onPrevenir(g, c[g].changes)}
                            title="La fenêtre d'envoi s'ouvre avec ces seules personnes, un message « [Modification] » et la convocation à jour"><i className="fa-solid fa-paper-plane" /> {LIB[g][2]} ({c[g].changes.length})</button>))}</div>}
                {ouvert && retires.length > 0 && (
                    <div className="po-prevenir-retires">
                        {retires.map((x) => (
                            <div key={x.envoi_id} className="po-prevenir-ret">
                                <span><b>{x.nom || x.adresse}</b> <span className="po-hint">— {x.sujet} · {x.adresse}</span></span>
                                {modifiable && <button type="button" className="po-btn po-btn-outline po-btn-sm" onClick={() => annuler(x)} title="Un court message d'annulation, pré-rempli, s'ouvre dans votre messagerie"><i className="fa-solid fa-envelope-open" /> Prévenir dans ma messagerie</button>}
                            </div>
                        ))}
                    </div>
                )}
                {erreur && <div className="po-erreur" style={{ marginTop: 6 }}>{erreur}</div>}
            </div>
        </div>
    );
}

/** Le menu « Envoyer les convocations ▾ ». */
export function MenuEnvoi({ planning, modifiable, onChoisir }) {
    const [ouvert, setOuvert] = useState(false);
    const zone = useRef(null);
    useEffect(() => {
        if (!ouvert) return undefined;
        const fermer = (e) => { if (zone.current && !zone.current.contains(e.target)) setOuvert(false); };
        document.addEventListener('mousedown', fermer);
        return () => document.removeEventListener('mousedown', fermer);
    }, [ouvert]);
    const dests = destinatairesConvocation(planning);
    const nEtu = dests.filter((d) => d.type === 'etudiant').length, nMaitres = dests.filter((d) => d.type === 'maitre').length;
    const choisir = (g) => { setOuvert(false); onChoisir(g); };
    return (
        <span className="po-envoi-zone" ref={zone}>
            <button type="button" className="po-btn po-btn-or" disabled={!modifiable || !nEtu} onClick={() => setOuvert((o) => !o)}
                    title={!modifiable ? 'Lecture seule : vous ne pouvez pas envoyer' : !nEtu ? 'Aucun passage placé' : 'Un message personnel par destinataire, vu et corrigé avant de partir'}>
                <i className="fa-solid fa-paper-plane" /> Envoyer les convocations <i className="fa-solid fa-caret-down" />
            </button>
            {ouvert && (
                <div className="po-envoi-menu" role="menu">
                    <button type="button" role="menuitem" onClick={() => choisir('candidat')}><i className="fa-solid fa-user-graduate" />
                        <span><b>Aux candidats</b><span className="po-hint">un message personnel par étudiant : son jour, son horaire, sa salle, son jury</span></span><span className="po-envoi-n">{nEtu}</span></button>
                    {nMaitres > 0 && <button type="button" role="menuitem" onClick={() => choisir('maitre')}><i className="fa-solid fa-briefcase" />
                        <span><b>Aux maîtres de stage et d'apprentissage</b><span className="po-hint">un message par maître, selon l'étudiant : le passage de SON stagiaire ou apprenti</span></span><span className="po-envoi-n">{nMaitres}</span></button>}
                    <button type="button" role="menuitem" onClick={() => choisir('jury')}><i className="fa-solid fa-chalkboard-user" />
                        <span><b>Aux jurys</b><span className="po-hint">un message par enseignant : la liste de ses passages</span></span></button>
                    <div className="po-envoi-pied"><i className="fa-solid fa-circle-info" /> La fenêtre d'envoi est celle du Suivi de stage : vous voyez et corrigez chaque message avant qu'il parte.</div>
                </div>
            )}
        </span>
    );
}

/**
 * Prépare l'envoi puis ouvre la fenêtre (ou la liste de secours). `outils` : { entete, modelesCv, logos, codeDept } de
 * l'onglet Documents (en-tête complet, modèles de convocation, logos réduits).
 */
export function EnvoiConvocations({ departementId, planning, genre, outils: outilsInitiaux, onFermer, onTermine, seulement = null, modification = false }) {
    // figés à l'ouverture : l'onglet recrée l'objet à chaque affichage (sinon la préparation repartirait en boucle)
    const outils = useRef(outilsInitiaux).current;
    const [brouillon, setBrouillon] = useState(null);
    const [erreur, setErreur] = useState(null);
    // « Modèles de courriel… » (2026-10-28, généralisé depuis le Planning annuel) : { typeMail, resoudre }
    const [editeur, setEditeur] = useState(null);
    useEffect(() => {
        let vivant = true;
        planningOrauxAPI.preparerEnvoi(planning.id, departementId, genre, modification)
            .then((r) => {
                if (!vivant) return;
                const b = r.data;
                if (b.joindre_convocation && !outils.entete?.etat?.complet) {
                    setErreur({ message: 'En-tête des documents incomplet : les convocations ne peuvent pas être fabriquées.',
                                detail: `À compléter dans Administration → Mon département → En-tête des documents (${(outils.entete?.etat?.manquants || []).join(', ') || 'en-tête absent'}), ou décochez « Joindre sa convocation (PDF) » dans le modèle de courriel.` });
                    return;
                }
                // PO.5p : « Relancer les sans réponse » / « Inviter les pas encore invités » — seules ces lignes cochées
                if (seulement) b.etudiants = b.etudiants.map((x) => ({ ...x, coche: !!x.coche && seulement.includes(x.dossier_id) }));
                setBrouillon(b);
            })
            .catch((e) => { if (vivant) setErreur({ message: erreurDe(e), detail: e?.response?.data?.detail || null }); });
        return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planning.id, departementId, genre, outils]);   // outils : figé (useRef) ; seulement : lu à l'ouverture

    /** Les convocations des lignes qui partent, d'après le planning RELU maintenant (et ses jetons de vérification). */
    const fabriquer = async (cles) => {
        if (!brouillon.joindre_convocation || !cles.length) return {};
        const pl = (await planningOrauxAPI.lire(planning.id, departementId)).data;
        const jetons = (await planningOrauxAPI.jetonsConvocation(planning.id, departementId, cles).catch(() => ({ data: { jetons: {} } }))).data.jetons || {};
        const dests = new Map(destinatairesConvocation(pl).map((d) => [d.cle, d]));
        const out = {};
        for (const k of cles) {
            const d = dests.get(k);
            if (!d) throw enErreurFenetre("Le planning a changé depuis l'ouverture de cette fenêtre", 'Fermez la fenêtre et rouvrez-la.');
            out[k] = base64De(genererConvocations({ planning: pl, destinataires: [d], modeles: outils.modelesCv.modeles, entete: outils.entete.entete,
                codeDept: outils.codeDept, jetons, logo: outils.logos?.logo, logoUniversite: outils.logos?.logoUniversite, compresser: true }).pdf);
        }
        return out;
    };
    const api = {
        lancer: async (dep, { edition, essai }) => {
            const cochees = (edition.etudiants || []).filter((x) => x.coche).map((x) => x.dossier_id);
            let cles = cochees;
            if (essai?.quoi === 'affiche') cles = [cochees[Math.max(0, Math.min(cochees.length - 1, parseInt(essai.index, 10) || 0))]].filter(Boolean);
            else if (essai?.quoi === 'trois') cles = cochees.slice(0, 3);
            let pieces;
            try { pieces = await fabriquer(cles); }
            catch (e) { throw e.response ? e : enErreurFenetre('Les convocations n\'ont pas pu être fabriquées : ' + erreurDe(e)); }
            return planningOrauxAPI.lancerEnvoi(planning.id, dep, { genre, etat: brouillon.etat, edition, essai, pieces });
        },
        etat: (dep, id) => planningOrauxAPI.etatEnvoi(planning.id, dep, id)
    };
    /** L'éditeur du modèle de cet envoi ; s'il a enregistré, le texte recomposé par le serveur revient dans la fenêtre. */
    const onModeles = (typeMail) => new Promise((resoudre) => setEditeur({ typeMail, resoudre }));
    const fermerEditeur = async (change) => {
        const { resoudre } = editeur;
        setEditeur(null);
        if (!change) { resoudre(null); return; }
        try {
            const b = (await planningOrauxAPI.preparerEnvoi(planning.id, departementId, genre, modification)).data;
            resoudre({ sujet: b.sujet, html: b.html });
        } catch (e) { resoudre(null); }
    };

    if (erreur) {
        return (
            <div className="stg-modale-fond" role="dialog" aria-modal="true" onClick={onFermer}>
                <div className="stg-modale" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
                    <div className="stg-modale-header"><i className="fa-solid fa-paper-plane" /> <span>Envoyer les convocations</span></div>
                    <div className="stg-modale-body"><div className="po-erreur" style={{ margin: 0 }}><i className="fa-solid fa-triangle-exclamation" /> <span><b>{erreur.message}</b>{erreur.detail ? <> — {erreur.detail}</> : null}</span></div></div>
                    <div className="stg-modale-pied"><button type="button" className="po-btn po-btn-outline" onClick={onFermer}>Fermer</button></div>
                </div>
            </div>
        );
    }
    if (!brouillon) {
        return <div className="stg-modale-fond"><div className="stg-modale" style={{ maxWidth: 420 }}><div className="stg-modale-body"><i className="fa-solid fa-circle-notch stg-tourne" /> Préparation des messages…</div></div></div>;
    }
    // la fenêtre du Stage, posée dans un hôte qui porte ses styles (« .stg-root ») à l'accent du module
    return (
        <div className="stg-root po-stg-hote po-envoi-hote">
            {brouillon.mode === 'smtp'
                ? <StageFenetrePromotion brouillon={brouillon} departementId={departementId} api={api} onFermer={onFermer} onTermine={onTermine} onModeles={onModeles} />
                : <SecoursUnParUn brouillon={brouillon} departementId={departementId} planning={planning} genre={genre} fabriquer={fabriquer} onFermer={onFermer} onTermine={onTermine} />}
            {editeur && (
                <FenetreModeleCourriel departementId={departementId} modifiable api={planningOrauxAPI}
                    charger={async () => ((await planningOrauxAPI.modelesCourriel(departementId)).data.modeles_mail || []).find((x) => x.type_mail === editeur.typeMail)}
                    presentation={{ ...MODELES_COURRIEL[editeur.typeMail], note: NOTE_COURRIEL, libellesCc: LIBELLES_CC, portee: "Le modèle est commun à tous les plannings d'oraux du département, avec ses versions." }}
                    onFermer={fermerEditeur} />
            )}
        </div>
    );
}

/** Sans serveur d'envoi : chaque message s'ouvre dans la messagerie de la personne, la convocation se télécharge. */
function SecoursUnParUn({ brouillon, departementId, planning, genre, fabriquer, onFermer, onTermine }) {
    const [lignes, setLignes] = useState(() => brouillon.etudiants.map((l) => ({ ...l })));
    const [faits, setFaits] = useState({});
    const [erreur, setErreur] = useState(null);
    const [occupe, setOccupe] = useState(null);
    const ouvrir = async (l) => {
        setOccupe(l.dossier_id); setErreur(null);
        try {
            const r = await planningOrauxAPI.mailtoEnvoi(planning.id, departementId, {
                genre, etat: brouillon.etat, cle: l.dossier_id,
                edition: { etudiants: [{ dossier_id: l.dossier_id, adresse: l.adresse, coche: true }], cc: (brouillon.cc || []).map((x) => x.adresse), cci: [], sujet: brouillon.sujet, html: brouillon.html }
            });
            setFaits((f) => ({ ...f, [l.dossier_id]: true }));
            window.location.href = r.data.mailto;
        } catch (e) { setErreur({ message: erreurDe(e), detail: e?.response?.data?.detail || null }); }
        finally { setOccupe(null); }
    };
    const telecharger = async (l) => {
        setOccupe(l.dossier_id); setErreur(null);
        try {
            const b64 = (await fabriquer([l.dossier_id]))[l.dossier_id];
            const a = document.createElement('a');
            a.href = 'data:application/pdf;base64,' + b64;
            a.download = `Convocation - ${`${l.prenom || ''} ${(l.nom || '').toUpperCase()}`.trim()}${genre === 'maitre' ? ' (maître)' : ''}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
        } catch (e) { setErreur({ message: erreurDe(e), detail: e?.response?.data?.detail || null }); }
        finally { setOccupe(null); }
    };
    const fermer = () => { if (Object.keys(faits).length) onTermine(true); onFermer(); };
    return (
        <div className="stg-modale-fond" role="dialog" aria-modal="true" onClick={fermer}>
            <div className="stg-modale fe-fenetre" style={{ maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
                <div className="stg-modale-header fe-titre"><i className="fa-solid fa-envelope-open" /> <span>{brouillon.titre} — dans votre messagerie, un par un</span></div>
                <div className="stg-modale-body fe-corps">
                    <div className="fe-retour fe-retour-ko" style={{ marginTop: 0 }}><i className="fa-solid fa-triangle-exclamation" /><span><b>Le serveur d'envoi n'est pas configuré</b> pour ce département (Administration → Envoi des courriels). Préparez chaque message dans votre messagerie : « Ouvrir » le remplit (objet, texte, destinataire){brouillon.joindre_convocation ? <> ; joignez la convocation téléchargée</> : null}. Le journal note « préparé ».</span></div>
                    <table className="fe-table" style={{ marginTop: 10 }}>
                        <thead><tr><th>{brouillon.colonne_ligne}</th><th>{brouillon.colonne_adresse}</th><th /></tr></thead>
                        <tbody>{lignes.map((l, i) => (
                            <tr key={l.dossier_id}>
                                <td>{l.a_libelle ? <><b>{l.a_libelle}</b> <span className="po-hint">pour {l.nom} {l.prenom}</span></> : <b>{l.nom} {l.prenom}</b>}<div className="po-hint">{l.info}</div></td>
                                <td><input className="input-sm fe-adr-input" value={l.adresse} placeholder="aucune adresse — saisissez-en une" onChange={(e) => setLignes((ls) => ls.map((x, k) => (k === i ? { ...x, adresse: e.target.value } : x)))} /></td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    {brouillon.joindre_convocation && <button type="button" className="po-btn po-btn-outline" disabled={!!occupe} onClick={() => telecharger(l)} title="La convocation de cette personne, à joindre à son message"><i className="fa-solid fa-download" /> Sa convocation</button>}{' '}
                                    <button type="button" className="po-btn po-btn-or" disabled={!!occupe || !l.adresse} onClick={() => ouvrir(l)}>{faits[l.dossier_id] ? <><i className="fa-solid fa-check" /> Rouvrir</> : <><i className="fa-solid fa-envelope-open" /> Ouvrir</>}</button>
                                </td>
                            </tr>
                        ))}</tbody>
                    </table>
                    {erreur && <div className="fe-retour fe-retour-ko"><i className="fa-solid fa-circle-xmark" /><span><b>{erreur.message}</b>{erreur.detail ? <> {erreur.detail}</> : null}</span></div>}
                </div>
                <div className="stg-modale-pied"><span className="po-hint" style={{ marginRight: 'auto' }}>{Object.keys(faits).length} / {lignes.length} préparé{Object.keys(faits).length > 1 ? 's' : ''}</span>
                    <button type="button" className="po-btn po-btn-outline" onClick={fermer}>Fermer</button></div>
            </div>
        </div>
    );
}
